import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { formsApi } from '@/api/forms';
import { analyticsApi } from '@/api/analytics';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export function AnalyticsPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const [selectedFormId, setSelectedFormId] = useState('');
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('day');

  const { data: formsData } = useQuery({
    queryKey: queryKeys.forms.all,
    queryFn: () => formsApi.list(),
  });

  const forms = formsData?.forms ?? [];
  const formId = selectedFormId || forms[0]?.id || '';

  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: queryKeys.analytics.trends(formId, period),
    queryFn: () => analyticsApi.getTrends(orgId, formId, period),
    enabled: !!formId && !!orgId,
  });

  const { data: referrersData } = useQuery({
    queryKey: queryKeys.analytics.referrers(formId),
    queryFn: () => analyticsApi.getReferrers(orgId, formId),
    enabled: !!formId && !!orgId,
  });

  const { data: completionsData } = useQuery({
    queryKey: queryKeys.analytics.completions(formId),
    queryFn: () => analyticsApi.getCompletions(orgId, formId),
    enabled: !!formId && !!orgId,
  });

  const trends = trendsData?.trends ?? [];
  const referrers = referrersData?.referrers ?? [];
  const completions = completionsData?.completions ?? [];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Analiticos</h1>

      {/* Controls */}
      <div className="flex gap-3 mb-6">
        <select
          value={formId}
          onChange={(e) => setSelectedFormId(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {forms.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as 'day' | 'week' | 'month')}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="day">Diario</option>
          <option value="week">Semanal</option>
          <option value="month">Mensal</option>
        </select>
      </div>

      {!formId ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
          Crie um formulario para ver os analiticos.
        </div>
      ) : (
        <div className="space-y-6">
          {/* Submission Trends */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold mb-4">Tendencias de Envios</h2>
            {trendsLoading ? (
              <div className="h-64 flex items-center justify-center text-gray-400">Carregando...</div>
            ) : trends.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-400">Sem dados ainda</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#10b981" fill="#10b98120" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Referrer Breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-semibold mb-4">Principais Origens</h2>
              {referrers.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-gray-400">Sem dados ainda</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={referrers} dataKey="count" nameKey="referrer" cx="50%" cy="50%" outerRadius={80} label={((props: any) => `${props.referrer} (${props.count})`) as any}>
                      {referrers.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Field Completion Rates */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-semibold mb-4">Taxa de Preenchimento dos Campos</h2>
              {completions.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-gray-400">Sem dados ainda</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={completions} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} unit="%" />
                    <YAxis dataKey="field" type="category" tick={{ fontSize: 12 }} width={100} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Bar dataKey="completionRate" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
